<template>
  <v-container fluid>
    <!-- Nav Drawer Component -->
    <v-navigation-drawer v-model="navExpanded" temporary app class="nav-drawer">
      <v-list nav dense>
        <v-list-item-group v-if="isLoggedIn">
          <a href="/">
            <v-img
              :src="getLogoImgUrl()"
              alt="logo"
              contain
              height="40"
              width="60"
              class="mx-auto my-4"
            />
          </a>

          <!-- Points store menu -->
          <v-list-group v-if="isParent" value="true">
            <template v-slot:activator>
              <v-list-item-title>
                <v-icon size="20" color="secondary" class="mr-2"
                  >fas fa-gem
                </v-icon>
                {{ user.points }}
              </v-list-item-title>
            </template>
            <v-list nav>
              <v-list-item
                v-for="item in pointsStoreMenuItems"
                :key="item.index"
                :to="{ name: item.route }"
              >
                <v-list-item-content>
                  <v-list-item-title v-text="item.title"></v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-list-group>
          <v-list-item v-if="isKid" :to="{ name: 'MyGifts' }">
            <v-list-item-title>
              <v-icon size="20" color="secondary" class="mr-2"
                >fas fa-heart
              </v-icon>
              My Rewards
            </v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isKid">
            <v-list-item-title>
              <v-icon size="20" color="secondary" class="mr-2"
                >fas fa-gem
              </v-icon>
              {{ user.points }}
            </v-list-item-title>
          </v-list-item>

          <!-- Catalog menu -->
          <v-list-group value="true">
            <template v-slot:activator>
              <v-list-item-title>
                <v-icon size="20" color="secondary" class="mr-2"
                  >fas fa-store
                </v-icon>
                Catalog
              </v-list-item-title>
            </template>
            <v-list nav>
              <v-list-item :to="{ name: 'Catalog' }" exact>
                <v-list-item-content>
                  <v-list-item-title>All Categories</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                v-for="(category, i) in categories"
                :key="i"
                :to="{
                  name: 'CategoryPage',
                  params: { categoryId: category.id }
                }"
              >
                <v-list-item-icon v-if="category.icon">
                  <v-icon v-text="category.icon"></v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title v-text="category.name"></v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-list-group>

          <v-list-item
            link
            v-if="isParent && !isInKidDetailOverview"
            :to="{ name: 'ParentDashboard' }"
          >
            <v-list-item-title>Kids</v-list-item-title>
          </v-list-item>

          <!-- Kid details menu -->
          <v-list-group v-if="isParent && isInKidDetailOverview" value="true">
            <template v-slot:activator>
              <v-list-item-title>
                <v-icon size="20" color="secondary" class="mr-2"
                  >fas fa-user
                </v-icon>
                Kid
              </v-list-item-title>
            </template>
            <v-list nav>
              <v-list-item
                v-for="item in kidDetailMenuItems"
                :key="item.index"
                :to="{ name: item.route }"
              >
                <v-list-item-content>
                  <v-list-item-title v-text="item.title"></v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-list-group>

          <v-list-item link :to="{ name: 'AccountSettings' }">
            <v-list-item-title>Settings</v-list-item-title>
          </v-list-item>
          <v-list-item link :to="{ name: 'Logout' }">
            <v-list-item-title>Logout</v-list-item-title>
          </v-list-item>
        </v-list-item-group>

        <v-list-item-group v-else>
          <a href="/">
            <v-img
              :src="getLogoImgUrl()"
              alt="logo"
              contain
              height="40"
              width="60"
              class="mx-auto my-4"
            />
          </a>
          <v-list-item link :to="{ name: 'Login' }">
            <v-list-item-title>Log in</v-list-item-title>
          </v-list-item>
          <v-list-item link :to="{ name: 'Signup' }">
            <v-list-item-title>Sign up</v-list-item-title>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </v-navigation-drawer>

    <!-- Header Links Component -->
    <v-app-bar color="white" elevate-on-scroll class="header-app-bar">
      <v-app-bar-nav-icon
        @click.stop="navExpanded = !navExpanded"
        class="hidden-lg-and-up"
      />
      <v-toolbar-title :class="{ 'mobile-logo': isMobile }">
        <a href="/">
          <v-img
            :src="getLogoImgUrl()"
            alt="logo"
            width="60"
            height="45"
            contain
          />
        </a>
      </v-toolbar-title>
      <v-spacer />
      <!-- Logged In Header Links -->
      <v-btn
        text
        v-if="isLoggedIn"
        :to="{ name: 'Catalog' }"
        color="black"
        class="hidden-sm-and-down font-weight-bold mx-2"
      >
        <v-icon size="20" color="secondary" class="mr-2">fas fa-store</v-icon>
        Catalog
      </v-btn>
      <v-btn
        text
        v-if="isLoggedIn && isParent"
        :to="{ name: 'PointsStore' }"
        color="black"
        class="hidden-sm-and-down font-weight-bold mx-2"
      >
        <v-icon size="20" color="secondary" class="mr-2">fas fa-gem</v-icon>
        {{ user.points }}
      </v-btn>
      <v-btn
        text
        v-if="isLoggedIn && isKid"
        color="black"
        class="hidden-sm-and-down font-weight-bold mx-2"
      >
        <v-icon size="20" color="secondary" class="mr-2">fas fa-gem</v-icon>
        {{ user.points }}
      </v-btn>
      <v-btn
        text
        v-if="isLoggedIn && isKid"
        :to="{ name: 'MyGifts' }"
        color="black"
        class="hidden-sm-and-down font-weight-bold mx-2"
      >
        <v-icon size="20" color="secondary" class="mr-2">fas fa-heart</v-icon>
        My Rewards
      </v-btn>
      <v-btn
        text
        v-if="isLoggedIn && isParent"
        :to="{ name: 'ParentDashboard' }"
        color="black"
        class="hidden-sm-and-down font-weight-bold mx-2"
        >Kids
      </v-btn>
      <SettingsMenu v-if="isLoggedIn" />

      <!-- Not Logged In Header Links -->
      <v-btn
        text
        v-if="!isLoggedIn && !isKidDomain"
        :to="{ name: 'Login' }"
        color="primary"
        class="hidden-sm-and-down font-weight-bold mx-2"
        >Log in
      </v-btn>
      <v-btn
        color="secondary"
        v-if="!isLoggedIn && !isKidDomain"
        :to="{ name: 'Signup' }"
        class="hidden-sm-and-down font-weight-bold mx-2"
        >Sign up
      </v-btn>
      <a href="https://easirewards.club" v-if="!isLoggedIn && isKidDomain"
        >Parent’s Website</a
      >
    </v-app-bar>
  </v-container>
</template>

<script>
import SettingsMenu from '@/components/SettingsMenu';

export default {
  name: 'Header',
  data() {
    return {
      kidDetailMenuItems: [
        {
          index: 0,
          title: 'Quests',
          route: 'KidQuests'
        },
        {
          index: 1,
          title: 'Profile',
          route: 'KidProfile'
        },
        {
          index: 2,
          title: 'Rewards',
          route: 'KidGifts'
        }
      ],
      navExpanded: false,
      currentCategory: 0,
      pointsStoreMenuItems: [
        {
          index: 0,
          title: 'Points Store',
          route: 'PointsStore'
        },
        {
          index: 1,
          title: 'Auto-Reload Settings',
          route: 'PointsAutoReloadSettings'
        },
        {
          index: 2,
          title: 'Purchase History',
          route: 'PointsPurchaseHistory'
        }
      ]
    };
  },

  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.mobile;
    },
    isInKidDetailOverview() {
      return (
        this.$route.name === 'KidQuests' ||
        this.$route.name === 'KidProfile' ||
        this.$route.name === 'KidGifts'
      );
    },
    categories() {
      return this.$store.state.products.categories;
    },
    isKidDomain() {
      return window.location.hostname.includes('easiclub');
    },
    isLoggedIn: function () {
      return this.$store.getters.isLoggedIn;
    },
    user: function () {
      return this.$store.getters.userInfo;
    },
    isKid: function () {
      return this.$store.getters.userInfo.role === 'child';
    },
    isParent: function () {
      return this.$store.getters.userInfo.role === 'parent';
    }
  },

  methods: {
    fetchCategories() {
      this.$http
        .get('/categories')
        .then(response => {
          this.$store.commit('addCategories', response.data.categories);
        })
        .catch(error => {
          console.error(error);
          this.$store.commit('setError', {
            message: 'Failed to retrieve categories information',
            type: 'error'
          });
        });
    },

    getLogoImgUrl() {
      if (window.location.hostname === 'easiclub') {
        return require('@/assets/logo_club_light_bg.svg');
      } else {
        return require('@/assets/logo_rewards_light_bg.svg');
      }
    }
  },

  components: { SettingsMenu },

  updated() {
    if (this.$validator.isEmptyArray(this.categories)) {
      this.fetchCategories();
    }
  }
};
</script>

<style lang="scss" scoped>
.header-app-bar {
  padding: 0% 7.5%;
}

.nav-drawer {
  .v-list-item__icon.v-list-group__header__append-icon {
    i {
      font-size: 14px !important;
    }
  }
}

.mobile-logo {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding-left: 0px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Header.vue